/*
 * Copyright 2026 Sony Corporation
 */

import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';

import { useAuth } from '../contexts/auth-context';
import { AuthError, type MfaRequired, type NewPasswordRequired } from '../services/cognito-user-management';

type Challenge = NewPasswordRequired | MfaRequired;

const message = (error: unknown): string =>
  error instanceof AuthError || error instanceof Error ? error.message : 'Sign-in failed.';

export const Login = () => {
  const { signIn, confirmSignedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as Location<{ from?: string } | null>;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [answer, setAnswer] = useState('');
  const [challenge, setChallenge] = useState<Challenge>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const finish = () => {
    confirmSignedIn();
    navigate(location.state?.from ?? '/worlds', { replace: true });
  };

  const submitCredentials = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const result = await signIn(email, password);
      if (result.kind === 'signed_in') {
        finish();
      } else {
        setChallenge(result);
      }
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  const submitChallenge = async (event: FormEvent) => {
    event.preventDefault();
    if (!challenge) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await challenge.complete(answer);
      finish();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  const newPassword = challenge?.kind === 'new_password_required';

  return (
    <Box className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Device Verse
          </Typography>

          {error && (
            <Alert severity="error" className="mb-4">
              {error}
            </Alert>
          )}

          {challenge ? (
            <form onSubmit={submitChallenge}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {newPassword
                    ? 'This account still has its temporary password. Choose a new one to continue.'
                    : 'Enter the verification code for this account.'}
                </Typography>
                <TextField
                  label={newPassword ? 'New password' : 'Verification code'}
                  type={newPassword ? 'password' : 'text'}
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                  autoFocus
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={busy} fullWidth>
                  Continue
                </Button>
              </Stack>
            </form>
          ) : (
            <form onSubmit={submitCredentials}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  fullWidth
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" disabled={busy} fullWidth>
                  Sign in
                </Button>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
